import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, Loader2, Mail, Plus, Save, Trash2, Video } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PROGRAMS = [["residency-bench-side", "Bench-side"], ["residency-remote-lab", "Remote Lab"], ["residency-solo-bench", "Solo Bench"]];
const LANGS = [["en", "English"], ["es", "Español"], ["fr", "Français"]];

function Field({ label, children }) {
  return <label className="block text-xs font-semibold text-slate-600 space-y-1"><span>{label}</span>{children}</label>;
}

function ListEditor({ items, onChange, testId, render, blank }) {
  const set = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1">{render(it, (v) => set(i, v), i)}</div>
          <Button size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testId}-remove-${i}`}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange([...items, blank])} data-testid={`${testId}-add`}><Plus size={13} className="mr-1" /> Add</Button>
    </div>
  );
}

function LangPanel({ c, onChange, lang }) {
  const set = (k, v) => onChange({ ...c, [k]: v });
  return (
    <div className="space-y-5" data-testid={`welcome-lang-panel-${lang}`}>
      <Field label="Personal video link (YouTube or Vimeo)">
        <Input value={c.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://vimeo.com/…" data-testid={`welcome-video-${lang}`} />
      </Field>
      {c.thumbnail && <img src={c.thumbnail} alt="" className="w-64 rounded-lg border border-slate-200" data-testid={`welcome-thumb-${lang}`} />}
      {!c.video_url && lang !== "en" && <p className="text-xs text-slate-500">Empty: the English video is used.</p>}
      <Field label="Welcome message">
        <Textarea rows={4} value={c.intro || ""} onChange={(e) => set("intro", e.target.value)} data-testid={`welcome-intro-${lang}`} />
      </Field>
      <Field label="Daily agenda">
        <ListEditor items={c.agenda || []} onChange={(v) => set("agenda", v)} testId={`welcome-agenda-${lang}`} blank={{ title: "", detail: "" }}
          render={(a, upd, i) => (
            <div className="grid sm:grid-cols-3 gap-2">
              <Input data-testid={`welcome-agenda-title-${lang}-${i}`} value={a.title} placeholder={`Day ${i + 1}: topic`} onChange={(e) => upd({ ...a, title: e.target.value })} />
              <Input data-testid={`welcome-agenda-detail-${lang}-${i}`} className="sm:col-span-2" value={a.detail} placeholder="What happens that day" onChange={(e) => upd({ ...a, detail: e.target.value })} />
            </div>
          )} />
      </Field>
      <Field label="Prep checklist">
        <ListEditor items={c.checklist || []} onChange={(v) => set("checklist", v)} testId={`welcome-checklist-${lang}`} blank=""
          render={(s, upd, i) => <Input data-testid={`welcome-checklist-item-${lang}-${i}`} value={s} onChange={(e) => upd(e.target.value)} />} />
      </Field>
    </div>
  );
}

export default function AdminResidencyWelcome() {
  const [pid, setPid] = useState(PROGRAMS[0][0]);
  const [lang, setLang] = useState("en");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => { setD(null); api.get(`/admin/residency-welcome/${pid}`).then((r) => setD(r.data)); }, [pid]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (r?.data?.content) setD(r.data); toast.success(typeof ok === "function" ? ok(r.data) : ok); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(""); }
  };
  if (!d) return <Loader2 className="animate-spin text-slate-400" />;
  const set = (k, v) => setD({ ...d, [k]: v });
  const save = () => run("save", () => api.put(`/admin/residency-welcome/${pid}`, { ...d, cohort_start: d.cohort_start || null, cohort_end: d.cohort_end || null }), "Welcome pack saved");
  const translate = () => window.confirm("Overwrite the Spanish and French text with a fresh translation of the saved English?") && run("tr", () => api.post(`/admin/residency-welcome/${pid}/translate`), "Spanish & French filled from English");
  const test = () => run("test", () => api.post(`/admin/residency-welcome/${pid}/test`, { language: lang }), (r) => `Test (${r.language.toUpperCase()}) sent to ${r.to}`);
  const tab = (active) => `px-3 py-1.5 rounded-md text-sm font-semibold ${active ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`;

  return (
    <div data-testid="admin-residency-welcome" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display font-black text-3xl text-slate-900">Residency Welcome Pack</h1>
        <p className="text-sm text-slate-600 mt-1">Sent automatically the moment a member pays, in their selected language. Save before sending a test.</p>
      </div>
      <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1">
        {PROGRAMS.map(([id, label]) => <button key={id} className={tab(pid === id)} onClick={() => setPid(id)} data-testid={`welcome-program-${id}`}>{label}</button>)}
      </div>
      <section className="rounded-2xl border border-slate-200 bg-white p-5 grid sm:grid-cols-2 gap-4">
        <Field label="Cohort start"><Input type="date" value={d.cohort_start || ""} onChange={(e) => set("cohort_start", e.target.value)} data-testid="welcome-cohort-start" /></Field>
        <Field label="Cohort end"><Input type="date" value={d.cohort_end || ""} onChange={(e) => set("cohort_end", e.target.value)} data-testid="welcome-cohort-end" /></Field>
        <Field label="Daily hours"><Input value={d.daily_hours} placeholder="9:00 AM – 4:00 PM ET" onChange={(e) => set("daily_hours", e.target.value)} data-testid="welcome-daily-hours" /></Field>
        <Field label="Location (in person)"><Input value={d.location} onChange={(e) => set("location", e.target.value)} data-testid="welcome-location" /></Field>
        <div className="sm:col-span-2"><Field label="Online join link (Remote Lab)"><Input value={d.online_link} onChange={(e) => set("online_link", e.target.value)} data-testid="welcome-online-link" /></Field></div>
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1">
            {LANGS.map(([id, label]) => <button key={id} className={tab(lang === id)} onClick={() => setLang(id)} data-testid={`welcome-lang-${id}`}>{label}</button>)}
          </div>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={translate} data-testid="welcome-translate">
            {busy === "tr" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Languages size={13} className="mr-1.5" />} Fill ES/FR from saved English
          </Button>
        </div>
        <LangPanel lang={lang} c={d.content[lang] || {}} onChange={(c) => setD({ ...d, content: { ...d.content, [lang]: c } })} />
      </section>
      <div className="flex flex-wrap gap-3 sticky bottom-4 rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-3 shadow-sm">
        <Button disabled={!!busy} onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="welcome-save">
          {busy === "save" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save
        </Button>
        <Button disabled={!!busy} variant="outline" onClick={test} data-testid="welcome-send-test">
          {busy === "test" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />} Send test ({lang.toUpperCase()}) to support inbox
        </Button>
        <span className="text-xs text-slate-500 self-center inline-flex items-center gap-1"><Video size={12} /> Thumbnails are pulled when you save.</span>
      </div>
    </div>
  );
}
